import { useState } from 'react';
import { useAlert, MoreOptionsDropdown } from '@uplift/ui-v2';

import { Box, Divider, MenuItem, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OrgManager, OrgManagerInviteStatus, OrgManagerRole, OrgManagerStatus } from '@uplift/api';
import { useHigherEdOrgQuery } from '@uplift/queries';

import { DeactivateDialog } from '../../../profile/DeactivateDialog';
import { useHigherEdHandlers } from '../hooks';
import { RevokeInviteModal, TransferOwnershipModal } from '../modals';
import { ManagementTableColumnRenderArgs, TransferOwnershipFormValues } from '../types';

export type ManagementActionsProps = ManagementTableColumnRenderArgs<keyof OrgManager> & {
  managers: OrgManager[];
  isCareApp?: boolean;
};

type ActionMenuItemProps = {
  title: string;
  subTitle?: string;
  onClick: () => void;
  selected?: boolean;
  disabled?: boolean;
  onDisabledClick?: () => void;
};

const ActionMenuItem: React.VFC<ActionMenuItemProps> = ({ onClick, title, subTitle, selected, disabled, onDisabledClick }) => {
  const { classes } = useStyles();

  const menuItem = (
    <MenuItem onClick={onClick} className={selected && classes.actionItemSelected} disabled={disabled}>
      <Box display="flex" flexDirection="column" >
        <Typography variant="body1">{title}</Typography>
        {subTitle && (
          <Typography variant="caption" color="textSecondary" component="div" data-detail-label>
            {subTitle}
          </Typography>
        )}
      </Box>
    </MenuItem>
  );

  if (disabled && onDisabledClick) {
    return <span onClick={onDisabledClick}>{menuItem}</span>;
  }
  return menuItem;
};

export const ManagementActions: React.VFC<ManagementActionsProps> = ({ row, managers, isCareApp }) => {
  const [ deactivateModalOpen, setDeactivateModalOpen ] = useState<boolean>(false);
  const [ revokeModalOpen, setRevokeModalOpen ] = useState<boolean>(false);
  const [ transferOwnershipModalOpen, setTransferOwnershipModalOpen ] = useState<boolean>(false);
  const { classes } = useStyles();
  const alert = useAlert();
  const {
    handleTransferOwnership,
    handlePromoteManager,
    handleDemoteManager,
    handleDeactivateManager,
    handleActivateManager,
    handleRevokeManagerInvite
  } = useHigherEdHandlers(row.orgId);

  const managerId = row?.user?.id;
  const isActive = row?.status === OrgManagerStatus.active;
  const isDeactivated = row?.inviteStatus === OrgManagerInviteStatus.accepted && row?.status === OrgManagerStatus.inactive;
  const isInvited = row?.inviteStatus === OrgManagerInviteStatus.invited && row?.status === OrgManagerStatus.inactive;

  const { data: orgData } = useHigherEdOrgQuery(row?.orgId, { enabled: !!row?.orgId });

  const noActiveManagersExceptOwner = managers
    .filter(manager => manager?.role !== OrgManagerRole.owner)
    .every(manager => manager?.status === OrgManagerStatus.inactive);

  const items = [];
  const isTransferOwnershipDisabled = !isActive || noActiveManagersExceptOwner;

  switch (row.role) {
    case OrgManagerRole.owner:
      items.push(<ActionMenuItem onClick={() => setTransferOwnershipModalOpen(true)} key="owner" title="Transfer ownership" subTitle="Assign a new organization owner" disabled={isTransferOwnershipDisabled} onDisabledClick={() => alert.info('There are no active managers to transfer ownership to')}/>);
      break;
    case OrgManagerRole.staff:
      if (isInvited) items.push(<ActionMenuItem onClick={() => setRevokeModalOpen(true)} key="revoke" title="Revoke Invite" />);
      if (isActive) {
        items.push(<ActionMenuItem onClick={() => handlePromoteManager(managerId)} key="admin" title="Promote to Admin" subTitle="Can invite staff and make referrals" disabled={!isActive} />);
        items.push(<Divider className={classes.divider} />);
        items.push(<ActionMenuItem onClick={() => setDeactivateModalOpen(true)} key="deactivate" title="Deactivate account" />);
      }
      if (isDeactivated) items.push(<ActionMenuItem onClick={() => handleActivateManager(managerId)} key="reactivate" title="Activate account" />);
      break;
    case OrgManagerRole.admin:
      if (isInvited) items.push(<ActionMenuItem onClick={() => setRevokeModalOpen(true)} key="revoke" title="Revoke Invite" />);
      if (isActive) {
        items.push(<ActionMenuItem onClick={() => handleDemoteManager(managerId)} key="staff" title="Demote to Staff" subTitle="Can make referrals" disabled={!isActive} />);
        items.push(<Divider className={classes.divider} />);
        items.push(<ActionMenuItem onClick={() => setDeactivateModalOpen(true)} key="deactivate" title="Deactivate account" />);
      }
      if (isDeactivated) items.push(<ActionMenuItem onClick={() => handleActivateManager(managerId)} key="reactivate" title="Activate account" />);
      break;
  }

  const handleDeactivateSubmit = async () => {
    if (isInvited) {
      await handleRevokeManagerInvite(row);
    } else {
      await handleDeactivateManager(managerId);
    }
    setDeactivateModalOpen(false);
  };

  const handleRevokeSubmit = async () => {
    await handleRevokeManagerInvite(row);
    setRevokeModalOpen(false);
  };

  const handleTransferOwnershipSubmit = async (values: TransferOwnershipFormValues) => {
    await handleTransferOwnership(values, managers);
    setTransferOwnershipModalOpen(false);
  };

  return (
    <>
      <DeactivateDialog
        name={`${row?.user?.firstName} ${row?.user?.lastName}`}
        meta={{ orgName: orgData?.name }}
        onSubmit={handleDeactivateSubmit}
        onCancel={() => setDeactivateModalOpen(false)}
        type="manager"
        open={deactivateModalOpen}
      />
      <RevokeInviteModal
        name={`${row?.user?.firstName} ${row?.user?.lastName}`}
        email={row?.user?.email}
        onSubmit={handleRevokeSubmit}
        onCancel={() => setRevokeModalOpen(false)}
        open={revokeModalOpen}
      />
      <TransferOwnershipModal
        isCareApp={isCareApp}
        manager={row}
        onSubmit={handleTransferOwnershipSubmit}
        onCancel={() => setTransferOwnershipModalOpen(false)}
        open={transferOwnershipModalOpen}
      />
      <Box alignItems="right">
        <MoreOptionsDropdown variant="vertical">
          {items}
        </MoreOptionsDropdown>
      </Box>
    </>
  );
};

const useStyles = makeStyles()(theme => ({
  divider: {
    marginBottom: theme.spacing(1)
  },
  actionItemSelected: {
    backgroundColor: theme.palette.secondary.background
  }
}));
